/* @flow */

import clsx from 'clsx';
import * as React from 'react';
import { connect } from 'react-redux';

import type { BasicFunction, KeyValuePair } from '../../../helpers/jsHelpers/types';
import type { ParentalControlItemState } from '../../../helpers/parentalControl/constsAndTypes';
import type { ChannelMap } from '../../../libs/netgemLibrary/v8/types/Channel';
import type { NETGEM_API_V8_METADATA_PROGRAM, NETGEM_API_V8_METADATA_SERIES } from '../../../libs/netgemLibrary/v8/types/MetadataProgram';
import type { NETGEM_API_V8_METADATA_SCHEDULE } from '../../../libs/netgemLibrary/v8/types/MetadataSchedule';
import type { NETGEM_API_V8_PURCHASE_INFO } from '../../../libs/netgemLibrary/v8/types/PurchaseInfo';
import type { NETGEM_API_V8_RIGHTS } from '../../../libs/netgemLibrary/v8/types/Rights';
import type { NETGEM_API_VIEWINGHISTORY } from '../../../libs/netgemLibrary/v8/types/ViewingHistory';
import type { BO_PURCHASE_LIST_TYPE } from '../../../redux/netgemApi/actions/videofutur/types/purchase';
import type { CombinedReducers } from '../../../redux/reducers';
import type { Dispatch } from '../../../redux/types/types';
import type { CARD_DATA_MODAL_TYPE } from '../../modal/cardModal/CardModalConstsAndTypes';
import type { MESSAGE_MODAL_TYPE } from '../../modal/messageModal/MessageModal';
import type { PIN_DATA_MODAL_TYPE } from '../../modal/pinModal/PinModalConstsAndTypes';
import type { CloseCallbackType } from '../../modal/vodPurchaseModal/VodPurchaseModal';

import { APPLICATION_ID } from '../../../helpers/applicationCustomization/types';
import { formatAvailabilityTimestamp } from '../../../helpers/dateTime/Format';
import { logError, showDebug } from '../../../helpers/debug/debug';
import { LoadableStatus } from '../../../helpers/loadable/loadable';
import Localizer from '../../../helpers/localization';
import { Messenger, MessengerEvents } from '../../../helpers/messenger';
import { ParentalControlLockReason, ParentalControlLockState } from '../../../helpers/parentalControl/constsAndTypes';
import { showUnavailableContentModal } from '../../../helpers/parentalControl/helpers';
import ParentalControlManager from '../../../helpers/parentalControl/parentalControlManager';
import { type BASE_VOD_PURCHASE_DATA_TYPE, PendingOperationKind, PendingOperationReason, type VOD_PURCHASE_DATA_TYPE, storePendingOperation } from '../../../helpers/rights/pendingOperations';
import { isPlaybackGranted } from '../../../helpers/rights/playback';
import { getDistributorId } from '../../../helpers/ui/item/distributor';
import { ExtendedItemType } from '../../../helpers/ui/item/types';
import { getSynopsis, renderProgramDetails } from '../../../helpers/ui/metadata/Format';
import { PROGRAM_INFO, type VOD_STATUS_TYPE } from '../../../helpers/ui/metadata/Types';
import { arePurchaseListsDifferent } from '../../../helpers/ui/section/comparisons';
import { BOVodAssetStatus, getTimeLeftFromExpirationTime, getVodKind, getVodLocationFromVtiId, getVodStatusFromLocations } from '../../../helpers/videofutur/metadata';
import { getProgress } from '../../../helpers/viewingHistory/ViewingHistory';
import { ignoreIfAborted } from '../../../libs/netgemLibrary/helpers/cancellablePromise/promiseHelper';
import getTranslatedText from '../../../libs/netgemLibrary/v8/helpers/Lang';
import { ItemContent, type NETGEM_API_V8_FEED_ITEM, type NETGEM_API_V8_ITEM_LOCATION, VodKind } from '../../../libs/netgemLibrary/v8/types/FeedItem';
import { FEATURE_SUBSCRIPTION } from '../../../redux/appConf/constants';
import { RegistrationType } from '../../../redux/appRegistration/types/types';
import { hideModal, showMessageModal, showPinModal, showVodPurchaseModal } from '../../../redux/modal/actions';
import { getImageUrl } from '../../../redux/netgemApi/actions/v8/metadataImage';
import { getVodLocations } from '../../../redux/netgemApi/actions/v8/metadataLocation';
import { getPurchaseInfoPerAsset } from '../../../redux/netgemApi/actions/v8/purchaseInfo';
import AccessibleButton from '../../buttons/AccessibleButton';
import { PinModalKind } from '../../modal/pinModal/PinModalConstsAndTypes';
import { PictoArrowDown, PictoLock } from '../../pictos/Element';
import PricingVod, { ContextKind } from '../../pricingVod/PricingVod';
import './VodEpisode.css';
import WatchingStatus from '../../watchingStatus/WatchingStatus';

const IMAGE_WIDTH = 108;
const IMAGE_HEIGHT = 160;

type ReduxVodEpisodeDispatchToPropsType = {
  +localGetImageUrl: (assetId: string, width: number, height: number, signal?: AbortSignal) => Promise<string>,
  +localGetPurchaseInfoPerAsset: (id: string, channelId: string, signal?: AbortSignal) => Promise<NETGEM_API_V8_PURCHASE_INFO>,
  +localGetVodLocations: (locations: Array<NETGEM_API_V8_ITEM_LOCATION>, signal?: AbortSignal) => Promise<Array<NETGEM_API_V8_METADATA_SCHEDULE>>,
  +localHideModal: BasicFunction,
  +localShowMessageModal: (messageData: MESSAGE_MODAL_TYPE) => void,
  +localShowPinModal: (data: PIN_DATA_MODAL_TYPE) => void,
  +showVodPurchaseDialog: (vodPurchaseData: VOD_PURCHASE_DATA_TYPE) => void,
};

type ReduxVodEpisodeReducerStateType = {
  +channels: ChannelMap,
  +commercialOffers: KeyValuePair<Array<string>> | null,
  +defaultRights: NETGEM_API_V8_RIGHTS | null,
  +isDebugModeEnabled: boolean,
  +isRegisteredAsGuest: boolean,
  +isSubscriptionFeatureEnabled: boolean,
  +purchaseList: BO_PURCHASE_LIST_TYPE | null,
  +userRights: Array<string> | null,
  +viewingHistory: NETGEM_API_VIEWINGHISTORY,
  +viewingHistoryStatus: LoadableStatus,
};

type VodEpisodePropType = {
  +cardData: CARD_DATA_MODAL_TYPE,
  +isExpanded: boolean,
  +index: number,
  +item: NETGEM_API_V8_FEED_ITEM,
  +onToggleExpandedState: (index: number) => void,
  +programMetadata: NETGEM_API_V8_METADATA_PROGRAM | null,
  +seriesMetadata: NETGEM_API_V8_METADATA_SERIES,
};

type CompleteVodEpisodePropType = {
  ...VodEpisodePropType,
  ...ReduxVodEpisodeReducerStateType,
  ...ReduxVodEpisodeDispatchToPropsType,
};

type VodEpisodeStateType = {
  imageUrl: string | null,
  parentalControlState: ParentalControlItemState,
  purchaseInfo: NETGEM_API_V8_PURCHASE_INFO | null,
  vodLocations: Array<NETGEM_API_V8_METADATA_SCHEDULE> | null,
  vodStatus: VOD_STATUS_TYPE | null,
};

const InitialState: Readonly<VodEpisodeStateType> = Object.freeze({
  imageUrl: null,
  parentalControlState: {
    hasPinCode: false,
    lockReason: undefined,
    lockState: ParentalControlLockState.NotInitialized,
  } as ParentalControlItemState,
  purchaseInfo: null,
  vodLocations: null,
  vodStatus: null,
});

class VodEpisode extends React.PureComponent<CompleteVodEpisodePropType, VodEpisodeStateType> {
  abortController: AbortController;

  synopsis: ?string;

  // Location Id of purchased location
  viewingHistoryId: ?string;

  // VtiId of purchased location
  vtiId: ?number;

  constructor(props: CompleteVodEpisodePropType) {
    super(props);

    this.abortController = new AbortController();
    this.synopsis = null;
    this.viewingHistoryId = null;
    this.vtiId = null;

    this.state = { ...InitialState };
  }

  componentDidMount() {
    Messenger.on(MessengerEvents.PARENTAL_CONTROL_STATE_UPDATE, this.updateLockState);
    Messenger.on(MessengerEvents.PARENTAL_CONTROL_PIN_UPDATE, this.updateLockState);

    this.update();
  }

  componentDidUpdate(prevProps: CompleteVodEpisodePropType) {
    const { purchaseList } = this.props;
    const { purchaseList: prevPurchaseList } = prevProps;

    if (arePurchaseListsDifferent(purchaseList, prevPurchaseList)) {
      this.loadVodLocations();
    }
  }

  componentWillUnmount() {
    const { abortController } = this;

    abortController.abort('Component VodEpisode will unmount');

    Messenger.off(MessengerEvents.PARENTAL_CONTROL_STATE_UPDATE, this.updateLockState);
    Messenger.off(MessengerEvents.PARENTAL_CONTROL_PIN_UPDATE, this.updateLockState);
  }

  updateLockState: () => void = () => {
    const { item } = this.props;

    this.setState({ parentalControlState: ParentalControlManager.getItemStateFromItem(item) });
  };

  showDebugInfo = () => {
    const { props, state } = this;

    showDebug('SeriesSection VodEpisode', {
      instance: this,
      instanceFields: ['synopsis', 'viewingHistoryId', 'vtiId'],
      misc: { watchingStatus: this.getWatchingStatus() },
      props,
      propsFields: ['episodesMetadata', 'index', 'isExpanded', 'item', 'programMetadata', 'seriesMetadata'],
      state,
      stateFields: ['imageUrl', 'parentalControlState', 'purchaseInfo', 'vodLocations', 'vodStatus'],
    });
  };

  update = () => {
    const { programMetadata } = this.props;

    this.viewingHistoryId = null;
    this.vtiId = null;
    this.synopsis = getSynopsis(programMetadata, Localizer.language);

    this.setState({ ...InitialState });

    this.updateLockState();

    this.loadImage();
    this.loadVodLocations();
    this.loadPurchaseInfo();
  };

  loadImage = () => {
    const {
      item: { selectedProgramId },
      localGetImageUrl,
    } = this.props;
    const {
      abortController: { signal },
    } = this;

    localGetImageUrl(selectedProgramId, IMAGE_WIDTH, IMAGE_HEIGHT, signal)
      .then((imageUrl: string) => {
        signal.throwIfAborted();

        this.setState({ imageUrl });
      })
      .catch((error) => ignoreIfAborted(signal, error));
  };

  loadVodLocations = () => {
    const {
      item: { locations },
      localGetVodLocations,
      purchaseList,
    } = this.props;
    const {
      abortController: { signal },
    } = this;

    if (!locations) {
      return;
    }

    localGetVodLocations(locations, signal)
      .then((vodLocations) => {
        signal.throwIfAborted();

        const vodStatus = getVodStatusFromLocations(vodLocations, purchaseList);
        const { viewingHistoryId, vtiId } = vodStatus;
        this.viewingHistoryId = viewingHistoryId;
        this.vtiId = vtiId;

        this.setState({
          vodLocations,
          vodStatus,
        });
      })
      .catch((error) => ignoreIfAborted(signal, error));
  };

  loadPurchaseInfo = () => {
    const {
      item: {
        id,
        purchasable,
        selectedLocation: { channelId, id: selectedLocationId },
      },
      localGetPurchaseInfoPerAsset,
    } = this.props;
    const {
      abortController: { signal },
    } = this;

    if (!purchasable || getVodKind(selectedLocationId) === VodKind.SVOD) {
      return;
    }

    if (channelId === undefined) {
      logError(`Channel Id is undefined for item ${id}`);
      return;
    }

    localGetPurchaseInfoPerAsset(id, channelId, signal)
      .then((purchaseInfo: NETGEM_API_V8_PURCHASE_INFO) => {
        signal.throwIfAborted();
        this.setState({ purchaseInfo });
      })
      .catch((error) => ignoreIfAborted(signal, error, () => this.setState({ purchaseInfo: null })));
  };

  playInternal = (isUnlockedAdultContent: boolean): void => {
    const { cardData, channels, commercialOffers, defaultRights, index, isRegisteredAsGuest, isSubscriptionFeatureEnabled, item, localHideModal, programMetadata, seriesMetadata, userRights } =
      this.props;
    const { purchaseInfo, vodLocations, vodStatus } = this.state;
    const { viewingHistoryId, vtiId } = this;

    if (!programMetadata) {
      return;
    }

    // TODO: change this when Continuous Watching works for other apps
    if (!isRegisteredAsGuest && process.env.REACT_APP_ID === APPLICATION_ID.FranceChannel) {
      // Tell card to play episode, so that continuous watching works
      Messenger.emit(MessengerEvents.CONTINUOUS_WATCHING_PLAY_FROM_EPISODE, index);
      return;
    }

    const { authority } = programMetadata;
    const locationMetadata = getVodLocationFromVtiId(vodLocations, vtiId);
    const distributorId = getDistributorId(vodLocations, purchaseInfo);

    const localCardData: CARD_DATA_MODAL_TYPE = {
      ...cardData,
      distributorId,
      programMetadata,
      seriesMetadata,
      viewingHistoryId,
      vtiId,
    };

    if (
      !isPlaybackGranted(
        isRegisteredAsGuest,
        isSubscriptionFeatureEnabled,
        localCardData,
        authority,
        vodStatus?.status === BOVodAssetStatus.Free ? ItemContent.NetgemSVOD : ItemContent.VODOrDeeplink,
        locationMetadata?.location,
        channels,
        commercialOffers,
        defaultRights,
        userRights,
      )
    ) {
      // Playback denied: log-in or subscribe page has been requested
      localHideModal();
      return;
    }

    Messenger.emit(MessengerEvents.OPEN_PLAYER, {
      authority,
      distributorId,
      isUnlockedAdultContent,
      item,
      locationId: locationMetadata?.location.id,
      openFromCard: true,
      programMetadata,
      seriesMetadata,
      type: ExtendedItemType.VOD,
      viewingHistoryId,
      vtiId,
    });
  };

  handlePlayButtonOnClick = (event: SyntheticMouseEvent<HTMLElement> | SyntheticTouchEvent<HTMLElement> | SyntheticKeyboardEvent<HTMLElement>) => {
    const { index, isExpanded, item, localShowMessageModal, localShowPinModal, onToggleExpandedState, programMetadata } = this.props;
    const {
      parentalControlState: { hasPinCode, lockReason, lockState },
    } = this.state;

    event.preventDefault();
    event.stopPropagation();

    if (!programMetadata || lockState === ParentalControlLockState.NotInitialized) {
      return;
    }

    if (!isExpanded) {
      // Expand item before play, so it will automatically reopen when player exits
      onToggleExpandedState(index);
    }

    if (!hasPinCode && lockReason === ParentalControlLockReason.AdultContent) {
      // Content is locked and cannot be unlocked
      showUnavailableContentModal(localShowMessageModal);
      return;
    }

    if (lockState === ParentalControlLockState.Locked) {
      // Episode is locked
      if (hasPinCode) {
        // Ask PIN code
        Messenger.once(MessengerEvents.MODAL_CLOSED, this.parentalControlContentUnlockClosedCallback);
        localShowPinModal({ item, kind: lockReason === ParentalControlLockReason.AdultContent ? PinModalKind.UnlockAdultContent : PinModalKind.UnlockContent });
      } else {
        // PIN code creation
        localShowPinModal({ kind: PinModalKind.CreateMaster });
      }
      return;
    }

    // Episode is not locked
    this.playInternal(false);
  };

  parentalControlContentUnlockClosedCallback = (isValidPin: boolean): void => {
    if (isValidPin) {
      this.playInternal(true);
    }
  };

  handleOnClick = (event: SyntheticMouseEvent<HTMLElement> | SyntheticTouchEvent<HTMLElement> | SyntheticKeyboardEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();

    const { index, isDebugModeEnabled, onToggleExpandedState } = this.props;
    const { altKey, ctrlKey } = event;

    if (isDebugModeEnabled && (ctrlKey || altKey)) {
      this.showDebugInfo();
    } else {
      onToggleExpandedState(index);
    }
  };

  vodPurchaseModalClosedCallback = (data?: CloseCallbackType): void => {
    const { item, programMetadata, seriesMetadata } = this.props;
    const { purchaseInfo, vodLocations } = this.state;

    if (!data) {
      // Purchase canceled
      return;
    }

    const { locationId, playNow, viewingHistoryId, vtiId } = data;

    if (playNow && vtiId !== undefined) {
      Messenger.emit(MessengerEvents.OPEN_PLAYER, {
        distributorId: getDistributorId(vodLocations, purchaseInfo),
        item,
        locationId,
        openFromCard: true,
        programMetadata,
        seriesMetadata,
        type: ExtendedItemType.VOD,
        viewingHistoryId,
        vtiId,
      });
    }
  };

  handlePurchaseButtonOnClick = (data: BASE_VOD_PURCHASE_DATA_TYPE): void => {
    const { cardData, isRegisteredAsGuest, item, localHideModal, localShowMessageModal, localShowPinModal, programMetadata, seriesMetadata, showVodPurchaseDialog } = this.props;
    const {
      parentalControlState: { hasPinCode, lockReason, lockState },
    } = this.state;
    const { vodPurchaseModalClosedCallback: closedCallback } = this;

    const { definition, displayPrice, distributorId, itemCount, licenseDuration, locationId, otherVtiId, purchaseType, vodLocationMetadata, vtiId } = data;

    if (!programMetadata) {
      return;
    }

    if (!hasPinCode && lockReason === ParentalControlLockReason.AdultContent) {
      // Content is locked and cannot be unlocked
      showUnavailableContentModal(localShowMessageModal);
      return;
    }

    const purchaseInternal = (isValidPin: boolean): void => {
      if (!isValidPin) {
        return;
      }

      const purchaseData: VOD_PURCHASE_DATA_TYPE = {
        definition,
        displayPrice,
        distributorId,
        item,
        itemCount,
        licenseDuration,
        locationId,
        otherVtiId,
        programMetadata,
        purchaseType,
        seriesMetadata,
        vodLocationMetadata,
        vtiId,
      };

      if (isRegisteredAsGuest) {
        // Guest mode: let's open the sign-in page
        storePendingOperation({
          ...cardData,
          pendingOperation: {
            operationType: PendingOperationKind.Purchase,
            purchaseData,
            reason: PendingOperationReason.RequireAccount,
          },
        });
        localHideModal();
        Messenger.emit(MessengerEvents.SHOW_SIGN_IN, PendingOperationKind.Purchase);
      } else {
        Messenger.once(MessengerEvents.MODAL_CLOSED, closedCallback);
        showVodPurchaseDialog(purchaseData);
      }
    };

    if (lockState !== ParentalControlLockState.Unlocked) {
      // Episode locked or not initialized yet
      if (hasPinCode) {
        // Ask PIN code
        Messenger.once(MessengerEvents.MODAL_CLOSED, purchaseInternal);
        localShowPinModal({ item: cardData?.item, kind: lockReason === ParentalControlLockReason.AdultContent ? PinModalKind.UnlockAdultContent : PinModalKind.UnlockContent });
      } else {
        // PIN code creation
        localShowPinModal({ kind: PinModalKind.CreateMaster });
      }
      return;
    }

    // Regular case: Episode can be rented or bought
    purchaseInternal(true);
  };

  getWatchingStatus = (): number | null => {
    const { item, programMetadata, seriesMetadata, viewingHistory, viewingHistoryStatus } = this.props;
    const { vodStatus } = this.state;

    const status = vodStatus?.status ?? BOVodAssetStatus.Unknown;
    if (status === BOVodAssetStatus.Locked) {
      // VodEpisode has been watched (at least partially) but its rent has expired
      return null;
    }

    if (viewingHistoryStatus !== LoadableStatus.Loaded) {
      return null;
    }

    return getProgress(viewingHistory, item, programMetadata, seriesMetadata, false, vodStatus);
  };

  renderWatchingStatus = (progress: number | null): React.Node => (
    <>
      <div />
      <WatchingStatus allowZeroProgress={false} onClick={this.handlePlayButtonOnClick} progress={progress} />
    </>
  );

  renderVodStatus = (progress: number | null): React.Node => {
    const { purchaseInfo, vodLocations, vodStatus } = this.state;

    if (!vodStatus) {
      return null;
    }

    const { expirationTime, status } = vodStatus;

    if (status === BOVodAssetStatus.Unknown || status === BOVodAssetStatus.FreeButAvailableInFuture) {
      // Purchase list not loaded yet or item will be available later (and free)
      return null;
    }

    if (status === BOVodAssetStatus.Bought || status === BOVodAssetStatus.Free || status === BOVodAssetStatus.Rented) {
      return this.renderWatchingStatus(progress);
    }

    return (
      <PricingVod
        context={ContextKind.VodEpisode}
        expirationTime={expirationTime}
        locationsMetadata={vodLocations}
        onClick={this.handlePurchaseButtonOnClick}
        purchaseInfo={purchaseInfo}
        status={status}
      />
    );
  };

  getTitle = (): string => {
    const { programMetadata } = this.props;

    if (!programMetadata) {
      return '';
    }

    const { titles } = programMetadata;
    return getTranslatedText(titles, Localizer.language);
  };

  renderPurchaseStatus = (isHeader: boolean): React.Node => {
    const { vodStatus } = this.state;

    if (!vodStatus) {
      return null;
    }

    const { availabilityTime, expirationTime, status } = vodStatus;
    let text: ?string = null;

    if (status === BOVodAssetStatus.Free) {
      // SVOD: asset is part of the subscription > don't repeat the same message for each episode
      return null;
    }

    if (status === BOVodAssetStatus.FreeButAvailableInFuture && typeof availabilityTime === 'number') {
      // SVOD: not available yet
      text = Localizer.localize('vod.pricing.availability.svod', { date: formatAvailabilityTimestamp(availabilityTime) });
    }

    if (status === BOVodAssetStatus.Rented) {
      // TVOD: asset already rented
      text = Localizer.localize('vod.pricing.tvod.rented');
      if (typeof expirationTime === 'number' && expirationTime > 0) {
        text = `${text} - ${Localizer.localize('vod.pricing.tvod.expire', { expiration: getTimeLeftFromExpirationTime(expirationTime) })}`;
      }
    } else if (!isHeader && status === BOVodAssetStatus.Bought) {
      // EST: asset already bought
      text = Localizer.localize('vod.pricing.est.bought');
    }

    if (text === null) {
      return null;
    }

    return <div className='purchaseStatus'>{text}</div>;
  };

  renderLockOverlay = (): React.Node => {
    const {
      cardData: { isUnlockedAdultContent },
    } = this.props;
    const {
      parentalControlState: { lockState },
    } = this.state;

    if (lockState !== ParentalControlLockState.Locked || isUnlockedAdultContent === true) {
      return null;
    }

    return (
      <div className='lockOverlay'>
        <PictoLock aria-hidden />
      </div>
    );
  };

  renderHeader = (): React.Node => {
    const { index, isExpanded, programMetadata } = this.props;

    const episodeIndex = programMetadata?.episodeOf?.episodeNumber;
    const episodeIndexStr = episodeIndex !== undefined ? `${episodeIndex} - ` : null;
    const progress = this.getWatchingStatus();

    return (
      <AccessibleButton
        aria-controls={`episode-panel-${index}`}
        aria-expanded={isExpanded}
        aria-labelledby={`episode-title-${index}`}
        className='header'
        id={`episode-header-${index}`}
        onClick={this.handleOnClick}
      >
        <div className='title' id={`episode-title-${index}`}>
          {episodeIndexStr}
          {this.getTitle()}
          {progress !== null && progress > 0 ? <span className='screenReaderOnly'>{Localizer.localize('accessibility.content.play_progress', { progress })}</span> : null}
        </div>
        <div className='actions'>
          {this.renderPurchaseStatus(true)}
          {this.renderVodStatus(progress)}
          <PictoArrowDown aria-hidden className='arrow' forceHoverEffect />
        </div>
      </AccessibleButton>
    );
  };

  renderContent = (): React.Node => {
    const { index, isExpanded, item, programMetadata } = this.props;
    const { imageUrl, vodLocations } = this.state;
    const { synopsis } = this;

    const imageStyle = imageUrl ? { backgroundImage: `url(${imageUrl})` } : {};

    return (
      <div aria-hidden={!isExpanded} aria-labelledby={`episode-header-${index}`} className='content' id={`episode-panel-${index}`} role='region'>
        <div className='coverWrapper'>
          <div className='cover' style={imageStyle} />
          {this.renderLockOverlay()}
        </div>
        <div className='details'>
          <div className='programInfo'>{renderProgramDetails(item, programMetadata, vodLocations, PROGRAM_INFO.Duration | PROGRAM_INFO.ParentalGuidance)}</div>
          <div className='synopsis'>
            <span className='screenReaderOnly'>{Localizer.localize('accessibility.content.synopsis')}</span>
            {synopsis}
          </div>
          {this.renderPurchaseStatus(false)}
        </div>
      </div>
    );
  };

  render(): React.Node {
    const { isExpanded } = this.props;

    return (
      <div className={clsx('episode', 'vod', isExpanded && 'expanded')}>
        {this.renderHeader()}
        {this.renderContent()}
      </div>
    );
  }
}

const mapStateToProps = (state: CombinedReducers): ReduxVodEpisodeReducerStateType => {
  return {
    channels: state.appConfiguration.deviceChannels,
    commercialOffers: state.appConfiguration.rightsCommercialOffers,
    defaultRights: state.appConfiguration.rightsDefault,
    isDebugModeEnabled: state.appConfiguration.isDebugModeEnabled,
    isRegisteredAsGuest: state.appRegistration.registration === RegistrationType.RegisteredAsGuest,
    isSubscriptionFeatureEnabled: state.appConfiguration.features[FEATURE_SUBSCRIPTION],
    purchaseList: state.appRegistration.registration === RegistrationType.RegisteredAsGuest ? {} : state.ui.purchaseList,
    userRights: state.appConfiguration.rightsUser,
    viewingHistory: state.ui.viewingHistory,
    viewingHistoryStatus: state.ui.viewingHistoryStatus,
  };
};

const mapDispatchToProps = (dispatch: Dispatch): ReduxVodEpisodeDispatchToPropsType => {
  return {
    localGetImageUrl: (assetId: string, width: number, height: number, signal?: AbortSignal): Promise<string> =>
      dispatch(
        getImageUrl(
          {
            assetId,
            height,
            width,
          },
          signal,
        ),
      ),

    localGetPurchaseInfoPerAsset: (id: string, channelId: string, signal?: AbortSignal): Promise<NETGEM_API_V8_PURCHASE_INFO> => dispatch(getPurchaseInfoPerAsset(id, channelId, signal)),

    localGetVodLocations: (locations: Array<NETGEM_API_V8_ITEM_LOCATION>, signal?: AbortSignal): Promise<Array<NETGEM_API_V8_METADATA_SCHEDULE>> => dispatch(getVodLocations(locations, signal)),

    localHideModal: (): void => dispatch(hideModal()),

    localShowMessageModal: (messageData: MESSAGE_MODAL_TYPE): void => dispatch(showMessageModal(messageData)),

    localShowPinModal: (data: PIN_DATA_MODAL_TYPE): void => dispatch(showPinModal(data)),

    showVodPurchaseDialog: (vodPurchaseData: VOD_PURCHASE_DATA_TYPE) => dispatch(showVodPurchaseModal(vodPurchaseData)),
  };
};

const VodEpisodeView: React.ComponentType<VodEpisodePropType> = connect(mapStateToProps, mapDispatchToProps)(VodEpisode);

export default VodEpisodeView;
